/**
 * Ce que l'écran d'accueil écrit, et rien d'autre.
 *
 * Fonctions PURES, comme `ot.ts`, `parc.ts` et `finance.ts` : ce qui est ici se
 * vérifie sans navigateur, sans base et sans session. `EcranAccueil.tsx`
 * dessine et décide ; ce fichier ne fait que mettre en mots.
 *
 * Cadré avec l'utilisateur le 07/09/2026, refait le 08/09 sur la maquette
 * `docs/maquettes/accueil.html` — la seule qui fasse foi, la version du 07/09
 * ayant été perdue avec l'espace de travail où elle vivait.
 */

/**
 * Une phrase du résumé, coupée en trois.
 *
 * POURQUOI COUPÉE, ET NON UNE CHAÎNE. Le chiffre s'affiche en couleur au
 * milieu de la phrase. Rendre une chaîne obligerait l'écran à y retrouver le
 * nombre pour l'entourer — donc à faire de la mise en forme avec une
 * expression régulière, qui se tromperait le jour où la phrase contiendrait
 * deux nombres. Ici, c'est cette fonction qui sait où est le chiffre, parce
 * que c'est elle qui l'a écrit.
 *
 * `avant` et `apres` peuvent être vides ; `fort` aussi, quand la phrase ne
 * porte aucun chiffre (« Rien de prévu aujourd'hui. »).
 */
export interface Phrase {
  readonly avant: string;
  readonly fort: string;
  readonly apres: string;
}

/**
 * Ce dont le résumé a besoin — volontairement PLUS PETIT que la réponse de
 * l'API.
 *
 * Ce fichier ne connaît pas `Accueil` et n'importe rien : lui passer la
 * réponse entière le rendrait dépendant de sa forme, et un test devrait
 * fabriquer trente champs pour en éprouver trois.
 */
export interface EtatResume {
  readonly journee: { readonly faites: number; readonly total: number };
  readonly aAffecter: number;
  readonly litiges: {
    readonly combien: number;
    /** L'âge du plus ancien, en jours. Nul quand il n'y en a aucun. */
    readonly plusAncienJours: number | null;
  };
  readonly bons: { readonly combien: number; readonly palettes: number };
}

/** Le « s » du pluriel, ou rien. Écrit une fois plutôt que douze. */
function s(combien: number): string {
  return combien > 1 ? 's' : '';
}

/**
 * Le prénom, tiré du nom.
 *
 * `utilisateurs` N'A QU'UNE COLONNE `nom` — vérifié dans le socle
 * `20260901093000_socle.sql` l. 96 : il n'existe pas de colonne `prenom`.
 * L'utilisateur a choisi le 08/09/2026 d'en prendre le PREMIER MOT plutôt que
 * d'ajouter une colonne.
 *
 * LA LIMITE EST CONNUE ET ASSUMÉE : une fiche saisie « Diallo Mohamed »
 * donnera « Bonjour Diallo ». La règle vit à l'affichage, la base n'est pas
 * touchée, et le jour où une colonne `prenom` existera, seule cette fonction
 * changera.
 *
 * Rend une chaîne VIDE si le nom n'en contient aucun : c'est à l'écran de
 * décider quoi montrer, pas à cette fonction d'inventer un « Bonjour ? ».
 */
export function prenomDit(nom: string): string {
  const mots = nom
    .trim()
    .split(/\s+/)
    .filter((mot) => mot.length > 0);
  if (mots.length === 0) return '';
  return mots[0] as string;
}

/**
 * La date du jour, en toutes lettres : « Lundi 7 septembre 2026 ».
 *
 * `Intl` fait le travail et rend « lundi 7 septembre 2026 », en minuscule.
 * La capitale est posée ici parce que la phrase commence par ce mot ; CSS
 * pourrait le faire, mais alors le texte enregistré et le texte affiché ne
 * seraient plus les mêmes, et un test ne pourrait plus rien en dire.
 *
 * La date est passée en paramètre plutôt que lue ici : une fonction qui lit
 * l'heure ne se teste pas deux fois de la même façon.
 */
export function dateDite(quand: Date): string {
  const dit = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(quand);
  if (dit.length === 0) return dit;
  return dit.charAt(0).toUpperCase() + dit.slice(1);
}

/**
 * Le nom du mois en cours, pour le titre de la bande des chiffres.
 * « Septembre 2026 ». Même capitale, même raison.
 */
export function moisDit(quand: Date): string {
  const dit = new Intl.DateTimeFormat('fr-FR', {
    month: 'long',
    year: 'numeric',
  }).format(quand);
  if (dit.length === 0) return dit;
  return dit.charAt(0).toUpperCase() + dit.slice(1);
}

/**
 * L'ancienneté, en jours, dite comme on la dit.
 *
 * C'est elle qui fait l'urgence d'un litige, pas la date. « depuis 73 jours »
 * se comprend d'un coup d'œil ; « ouvert le 26/06 » demande un calcul mental
 * que personne ne fait.
 */
export function ancienneteDite(jours: number): string {
  if (jours <= 0) return 'aujourd’hui';
  if (jours === 1) return 'depuis hier';
  return `depuis ${String(jours)} jours`;
}

/**
 * La part affrétée, en pourcentage entier.
 *
 * `null` QUAND IL N'Y A AUCUN ORDRE : zéro pour cent d'affrètement sur zéro
 * transport se lirait « nous faisons tout nous-mêmes », ce qui est une
 * affirmation qu'on n'a pas les moyens de faire. L'écran affiche alors un tiret.
 */
export function partAffretee(affretes: number, ordres: number): number | null {
  if (ordres <= 0) return null;
  return Math.round((affretes / ordres) * 100);
}

/**
 * Ce qu'il faut dire à côté de la marge, ou rien.
 *
 * LA MARGE MENT TANT QUE DES ORDRES N'ONT PAS DE COÛT. Le coût de revient
 * n'est figé que depuis le 07/09/2026, et seulement si les ressources sont
 * paramétrées ; sur un ordre sans coût, la marge vaut le prix de vente entier.
 *
 * Le chiffre serait donc faux ET FLATTEUR — c'est la pire combinaison, celle
 * qu'on lit sans se méfier. Cette phrase est la seule chose qui l'empêche, et
 * c'est elle qui justifie le trait ambre sous le montant.
 */
export function margeDouteuse(sansCout: number, ordres: number): string | null {
  if (sansCout <= 0) return null;
  if (sansCout >= ordres) {
    return 'Aucun coût de revient calculé : cette marge vaut le chiffre d’affaires.';
  }
  return `${String(sansCout)} ordre${s(sansCout)} sur ${String(ordres)} sans coût de revient : la marge est surévaluée.`;
}

/**
 * LES PHRASES DU RÉSUMÉ, dans l'ordre où elles tournent.
 *
 * Quatre au maximum, choisies par l'utilisateur le 08/09/2026 : la journée,
 * les ordres sans camion, les litiges ouverts, les bons de palette expirés.
 *
 * UNE PHRASE À ZÉRO NE S'AFFICHE PAS. « 0 litige ouvert » n'est pas une
 * nouvelle, et un carrousel qui fait défiler trois bonnes nouvelles fait
 * perdre celle qui compte. Le nombre de points sous la phrase suit donc le
 * nombre de phrases, et il peut n'y en avoir qu'un.
 *
 * LA JOURNÉE FAIT EXCEPTION et reste toujours en tête, même vide : « Rien de
 * prévu aujourd'hui » est une réponse, et son absence se lirait comme un
 * défaut de chargement.
 */
export function phrasesResume(etat: EtatResume): readonly Phrase[] {
  const phrases: Phrase[] = [phraseJournee(etat.journee.faites, etat.journee.total)];

  if (etat.aAffecter > 0) {
    phrases.push({
      avant: '',
      fort: `${String(etat.aAffecter)} ordre${s(etat.aAffecter)}`,
      apres: etat.aAffecter > 1 ? ' attendent un camion.' : ' attend un camion.',
    });
  }

  if (etat.litiges.combien > 0) {
    const combien = etat.litiges.combien;
    const age = etat.litiges.plusAncienJours;
    phrases.push({
      avant: '',
      fort: `${String(combien)} litige${s(combien)} ouvert${s(combien)}`,
      // L'ÂGE PEUT MANQUER : la réponse ne transporte que les huit premières
      // lignes. Il n'en manque jamais en pratique — huit lignes couvrent le
      // premier —, mais la phrase ne doit pas dépendre de ce hasard.
      apres: age === null ? '.' : `, le plus ancien ${ancienneteDite(age)}.`,
    });
  }

  if (etat.bons.combien > 0) {
    const combien = etat.bons.combien;
    const palettes = etat.bons.palettes;
    phrases.push({
      avant: '',
      fort: `${String(combien)} bon${s(combien)} de palette`,
      apres: `${combien > 1 ? ' ont expiré' : ' a expiré'} — ${String(palettes)} palette${s(palettes)} en jeu.`,
    });
  }

  return phrases;
}

/**
 * La phrase de la journée.
 *
 * PAS DE POURCENTAGE. « 38 % » ne dit pas s'il reste deux arrêts ou vingt, et
 * c'est la seule chose qui intéresse quelqu'un à neuf heures du matin.
 *
 * ZÉRO ÉTAPE N'EST PAS « 0 sur 0 » : c'est une journée sans transport, et il
 * faut le dire en toutes lettres. Un compteur à zéro se lit comme un retard.
 */
function phraseJournee(faites: number, total: number): Phrase {
  if (total === 0) {
    return { avant: 'Rien de prévu aujourd’hui.', fort: '', apres: '' };
  }
  if (faites >= total) {
    return {
      avant: 'Tout est fait — ',
      fort: `${String(total)} arrêt${s(total)}`,
      apres: '.',
    };
  }
  const reste = total - faites;
  return {
    avant: 'Il reste ',
    fort: `${String(reste)} arrêt${s(reste)}`,
    apres: ` à faire aujourd’hui, sur ${String(total)}.`,
  };
}
